<template>
  <div data-cy="invoice-details-form" :class="$style['invoice-details-form']">
    <LoadingScreen v-if="isLoading" />
    <div v-else>
      <VeeForm
        ref="form"
        class="c-form"
        :class="$style.form"
        :validation-schema="invoiceSchema"
        :initial-values="{
          beneficiaryId: activeBeneficiary,
          calendar: invoiceDueDate,
          memo,
        }"
        @submit="onSubmit"
      >
        <div v-if="showWarning" class="c-form__row grid">
          <Message v-if="threeDaysWarning" severity="warn" :closable="false">
            <p>
              {{
                $t(
                  'account.pages.pageVendors.invoiceDetailsForm.dueDateWarning',
                )
              }}
              <a
                :class="$style.link"
                :href="
                  addLocaleParam(
                    'https://help.clear.co/s/article/Invoice-processing-times',
                  )
                "
                target="_blank"
              >
                {{
                  $t(
                    'account.pages.pageVendors.invoiceDetailsForm.dueDateWarningLinkText',
                  )
                }}
              </a>
            </p>
          </Message>
        </div>
        <div class="c-form__row grid grid-cols-12">
          <div class="col-span-6">
            <DropdownGroup
              class="vendor-selection"
              :label="$t('common.vendor')"
              :placeholder="$t('common.selectVendor')"
              name="beneficiaryId"
              :options="vendorOptions"
              option-label="name"
              append-to="body"
              :filter="true"
              :reset-filter-on-hide="true"
              @change="switchVendor"
            >
              <template #option="slotProps">
                <div
                  :class="{
                    'dropdown-separation-line': slotProps.option.isLastItem,
                  }"
                >
                  <div>
                    {{ slotProps.option.name }}
                  </div>
                  <div v-if="slotProps.option.accountNumberLast4">
                    {{ slotProps.option.accountNumberLast4.type }}
                    {{ slotProps.option.accountNumberLast4.value }}
                    ({{ slotProps.option.currencyCode }})
                  </div>
                </div>
              </template>
            </DropdownGroup>
          </div>
          <div class="col-span-6">
            <InputDate
              :label="
                $t(
                  'account.pages.pageVendors.invoiceDetailsForm.invoiceDueDate',
                )
              "
              :max-date="maxInvoiceDate"
              :min-date="today"
              :manual-input="false"
              name="calendar"
              @date-select="updateDueDate"
              date-format="yy-mm-dd"
              :tooltip="
                $t(
                  'account.pages.pageVendors.invoiceDetailsForm.invoiceDueDateLabel',
                )
              "
            />
          </div>
        </div>
        <div :class="$style.paymentReference">
          <InputTextGroup
            :v-bind="memo"
            label="Payment Reference For Recipient"
            data-cy="memo"
            name="memo"
            @blur="(evt) => updateMemo(evt.target.value)"
          >
            <template #label>
              <button
                v-tooltip.top.focus="{
                  value: $t('account.pages.pageVendors.paymentReferenceTip'),
                  escape: true,
                }"
                type="button"
                :class="['pi pi-info-circle', $style.tooltip]"
              />
            </template>
          </InputTextGroup>
        </div>

        <NavigationButtons
          :back-label="$t('account.pages.pageVendors.back')"
          :forward-label="$t('account.pages.pageVendors.nextStep')"
          @back-navigate="goBack"
          forward-type="submit"
        />
      </VeeForm>
    </div>
  </div>
</template>

<script>
import { Form as VeeForm } from 'vee-validate';
import { object, string, date } from 'yup';
import { validateChildren } from '@/composables/validation';
import { mapGetters } from 'vuex';
import * as dateFns from 'date-fns';
import LoadingScreen from '@/components/LoadingScreen.vue';
import analytics, { SEGMENT_EVENTS } from '@/utils/analytics';
import {
  MAX_INVOICE_DUE_DATE_IN_MONTHS,
  DEFAULT_CURRENCY,
} from '@/data/payments';
import DropdownGroup from '@/components/forms/groups/DropdownGroup.vue';
import InputTextGroup from '@/components/forms/groups/InputTextGroup.vue';
import Message from 'primevue/message';
import addLocaleParam from '@/utils/add-locale-param';
import NavigationButtons from '@/components/buttons/NavigationButtons.vue';
import InputDate from '@/components/InputDate.vue';

export default {
  emits: [
    'dueDate',
    'update-memo',
    'next-tab',
    'prev-tab',
    'beneficiaryId',
    'update-repayment-schedule',
  ],
  components: {
    LoadingScreen,
    Message,
    VeeForm,
    InputTextGroup,
    DropdownGroup,
    NavigationButtons,
    InputDate,
  },
  props: {
    dueDate: {
      type: [String, Date],
      default: '',
    },
    beneficiaryId: {
      type: String,
      default: '',
    },
    amountCents: {
      type: Number,
      required: true,
    },
    selectedCurrency: {
      type: String,
      required: true,
    },
    memo: {
      type: String,
      required: true,
    },
  },
  setup() {
    const { hasError } = validateChildren();
    return {
      hasError,
    };
  },
  data() {
    return {
      isLoading: false,
      invoiceDueDate:
        this.dueDate &&
        new Date(this.convertToUtcDate(this.dueDate)) >= new Date()
          ? this.convertToUtcDate(this.dueDate)
          : null,
      today: new Date(),
      maxInvoiceDate: dateFns.addMonths(
        new Date(),
        MAX_INVOICE_DUE_DATE_IN_MONTHS,
      ),
      invoiceSchema: object({
        beneficiaryId: object()
          .test(
            'currencyMatch',
            'The currency for the selected vendor must match the invoice.',
            (val) => {
              if (!val || !val.currencyCode) return true;
              return this.selectedCurrency === val.currencyCode;
            },
          )
          .required('Vendor is required'),
        calendar: date()
          .nullable()
          .required('Invoice date is required')
          .min(dateFns.subDays(new Date(), 1), `Invoice cannot be past due`),
        memo: string()
          .required('Payment reference/memo is required')
          .max(70)
          .matches(
            /^[a-zA-Z0-9 ]*$/,
            'Payment reference must contain alphanumerical characters only',
          ),
      }),
    };
  },
  computed: {
    ...mapGetters([
      'beneficiaries',
      'getV2BeneficiaryById',
      'isDeprecateV0BeneficiariesEnabled',
    ]),
    // The following populates the available list of Beneficiaries the user can select
    // from during the Invoice upload flow.
    vendorOptions() {
      const vendorBeneOptions = this.beneficiaries.map((beneficiary) => ({
        name: this.isDeprecateV0BeneficiariesEnabled
          ? beneficiary.label
          : beneficiary.name,
        value: beneficiary.id,
        ...this.beneDropdownDetails(beneficiary),
      }));

      return [
        { name: this.$t('common.newVendor'), value: '+newvendor' },
        ...vendorBeneOptions,
      ];
    },
    showWarning() {
      return this.threeDaysWarning;
    },
    isDueDateSelected() {
      return this.invoiceDueDate && this.invoiceDueDate !== '';
    },
    threeDaysWarning() {
      // show warning if due date is not today + 3 days
      return (
        this.isDueDateSelected &&
        dateFns.differenceInBusinessDays(
          new Date(this.invoiceDueDate),
          new Date(),
        ) <= 3
      );
    },
    activeBeneficiary() {
      return this.vendorOptions.find(
        (option) => option.value === this.beneficiaryId,
      );
    },
  },
  methods: {
    addLocaleParam,
    convertToUtcDate(_date) {
      const dateUtc = new Date(_date)
        .toISOString()
        .replace('T', ' ')
        .substring(0, 19);
      return dateFns.parseISO(dateUtc);
    },
    updateDueDate(value) {
      this.invoiceDueDate = value;
      this.$emit('dueDate', this.formatDate(new Date(value)));
    },
    updateMemo(value) {
      this.$emit('update-memo', value);
    },
    formatDate(_date) {
      const year = _date.getFullYear();
      const month = `0${_date.getMonth() + 1}`.slice(-2);
      const day = `0${_date.getDate()}`.slice(-2);

      return `${year}-${month}-${day}`;
    },
    switchVendor({ value: beneficiaryObject }) {
      this.$emit('beneficiaryId', beneficiaryObject.value);
    },
    goBack() {
      analytics.track(SEGMENT_EVENTS.if_vendor_details_go_back_clicked);
      this.$emit('prev-tab');
    },
    async onSubmit(values) {
      analytics.track(SEGMENT_EVENTS.if_vendor_details_next_step_clicked, {
        vendorId: values.beneficiaryId,
      });
      await this.$store.dispatchApiAction('GET_FEES_SCHEDULE', {
        dueDate: this.invoiceDueDate,
        amountCents: this.amountCents,
        fromCurrency: this.selectedCurrency,
        toCurrency: DEFAULT_CURRENCY,
      });
      this.$emit('update-repayment-schedule');
      this.$emit('next-tab');
      this.updateMemo(this.memo);
    },
    accountNumberLast4(beneficiary) {
      let type, value;
      if (beneficiary.accountNumber) {
        type = 'Account';
        value = beneficiary.accountNumber;
      } else if (beneficiary.iban) {
        type = 'IBAN';
        value = beneficiary.iban;
      }

      return value && { type, value: `******${value.slice(-4)}` };
    },
    beneDropdownDetails(beneficiary) {
      // If deprecate v0 Beneficiaries is enabled; the beneficiary state objects are
      // actually v2 beneficiary records. Therefore we don't need to do anything special.
      if (this.isDeprecateV0BeneficiariesEnabled) {
        return {
          accountNumberLast4: this.accountNumberLast4(beneficiary),
          currencyCode: beneficiary.currencyCode,
        };
      }

      // Remove the following business logic when deprecate v0 beneficiaries feature flag is deprecated.
      let v2Beneficiary;
      if (beneficiary.beneficiaryServiceId) {
        v2Beneficiary = this.getV2BeneficiaryById(
          beneficiary.beneficiaryServiceId,
        );
      } else if (beneficiary.v2BenePayload) {
        v2Beneficiary = beneficiary.v2BenePayload;
      } else {
        return {};
      }

      return {
        accountNumberLast4: this.accountNumberLast4(v2Beneficiary),
        currencyCode: v2Beneficiary.currencyCode,
      };
    },
  },
  async mounted() {
    this.isLoading = true;
    if (this.isDeprecateV0BeneficiariesEnabled) {
      // get all active beneficiaries
      await this.$store.dispatchApiAction('GET_BENEFICIARIES', {
        status: 'active',
        type: 'vendor',
      });
    } else {
      await this.$store.dispatchApiAction('GET_BILL_BENEFICIARIES');

      // TODO: Fetch batch of v2 beneficiaries for the given business once that functionality is added as part of decoupling bills-svc from benes
      await Promise.all(
        this.beneficiaries.map((bene) =>
          bene.beneficiaryServiceId
            ? this.$store.dispatchApiAction('GET_V2_BENEFICIARY', {
                beneficiaryV2Id: bene.beneficiaryServiceId,
              })
            : undefined,
        ),
      );
    }

    this.isLoading = false;
  },
  watch: {
    dueDate(newDate) {
      analytics.track(SEGMENT_EVENTS.FE_DATE_PICKED, {
        submissionDate: this.convertToUtcDate(new Date()),
        invoiceDueDate: this.convertToUtcDate(newDate),
      });
      if (this.threeDaysWarning) {
        analytics.track(SEGMENT_EVENTS.FE_DATE_ERROR, {
          dateOfAction: this.convertToUtcDate(new Date()),
          datePicked: this.convertToUtcDate(newDate),
          type: 'invoice',
          error: 'Payment may arrive after invoice due date',
        });
      }
    },
  },
};
</script>

<style>
.vendor-selection .p-dropdown-label {
  text-transform: none;
}
.dropdown-separation-line {
  margin-bottom: -12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #dbdbdb;
}
</style>

<style lang="less" module>
.form {
  text-align: left;
  padding: 0;
}

.tooltip {
  color: @color-black;
  padding: 4px;
  cursor: help;
}

.link {
  color: #145bce;
}

.paymentReference {
  margin-top: 24px;
}

@media only screen and (min-width: 768px) {
  .invoice-details-form {
    min-width: 560px;
  }
}
</style>
